import { useState, useCallback, useEffect } from 'react';
import { toast } from 'sonner';
import {
  monthlyConsumptionApi,
  MonthlyConsumption,
  MonthlyConsumptionFilters,
} from '@/lib/api/monthly-consumption';

export const useMonthlyConsumption = (filters?: MonthlyConsumptionFilters) => {
  const [consumptions, setConsumptions] = useState<MonthlyConsumption[]>([]);
  const [currentConsumption, setCurrentConsumption] = useState<MonthlyConsumption | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [isRecalculating, setIsRecalculating] = useState(false);
  const [pagination, setPagination] = useState({
    total: 0,
    page: 1,
    limit: 10,
    totalPages: 1,
  });

  const fetchConsumptions = useCallback(async () => {
    setIsLoading(true);
    try {
      const response = await monthlyConsumptionApi.getAll(filters);
      setConsumptions(response.data);
      setPagination({
        total: response.total,
        page: response.page,
        limit: response.limit,
        totalPages: response.totalPages,
      });
      return response.data;
    } catch (err: any) {
      toast.error(err.response?.data?.message || 'Failed to load monthly consumptions');
      return [];
    } finally {
      setIsLoading(false);
    }
  }, [filters?.page, filters?.limit, filters?.contractId, filters?.companyId]);

  const fetchSpecificConsumption = useCallback(
    async (contractId: string, year: number, month: number) => {
      setIsLoading(true);
      try {
        const data = await monthlyConsumptionApi.getSpecific(contractId, year, month);
        setCurrentConsumption(data);
        return data;
      } catch (err: any) {
        toast.error(err.response?.data?.message || 'Failed to load monthly consumption details');
        return null;
      } finally {
        setIsLoading(false);
      }
    },
    []
  );

  const recalculateConsumption = useCallback(
    async (contractId: string, year: number, month: number) => {
      setIsRecalculating(true);
      try {
        await monthlyConsumptionApi.recalculate(contractId, year, month);
        toast.success('Monthly consumption recalculated successfully');

        // Refresh the data
        await fetchConsumptions();

        return true;
      } catch (err: any) {
        toast.error(err.response?.data?.message || 'Failed to recalculate monthly consumption');
        return false;
      } finally {
        setIsRecalculating(false);
      }
    },
    [fetchConsumptions]
  );

  useEffect(() => {
    fetchConsumptions();
  }, [fetchConsumptions]);

  return {
    consumptions,
    currentConsumption,
    isLoading,
    isRecalculating,
    pagination,
    fetchSpecificConsumption,
    recalculateConsumption,
    refreshConsumptions: fetchConsumptions,
  };
};
